import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  privacyService,
  parseRoles,
  MASKABLE_ROLES,
  type PiiPolicy,
  type PolicyPreview,
} from 'api/services/privacy.service';

/**
 * Who in this organisation sees a customer whole.
 *
 * <h3>Why the preview leads and the switch follows</h3>
 * The sibling page for the contact ceiling was built this way because a setting
 * turned on blind fails silently. The same is true here in both directions: the
 * endpoints could sit live and unreachable, the way the do-not-contact list
 * stayed at zero rows across 47 organisations; or masking could go on and only
 * then hide something the desk needed. So the page shows a REAL capture from
 * this organisation, masked under the settings being considered, before it
 * offers to save anything.
 *
 * <h3>What this page cannot promise</h3>
 * It says so on the page itself: masking applies to what the API returns, which
 * is the part that matters, but anyone who can already export or sync leads
 * still has the originals. A control described as more than it is gets trusted
 * more than it should be.
 */
const DataPrivacy: React.FC = () => {
  const [policy, setPolicy] = useState<PiiPolicy | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [roles, setRoles] = useState<string[]>(['BUSINESS_AGENT']);
  const [maskPhone, setMaskPhone] = useState(true);
  const [maskEmail, setMaskEmail] = useState(true);
  const [maskName, setMaskName] = useState(false);
  const [restrictToAssigned, setRestrictToAssigned] = useState(true);

  const [preview, setPreview] = useState<PolicyPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const p = await privacyService.get();
      if (!alive) return;
      if (p) {
        setPolicy(p);
        setEnabled(!!p.enabled);
        setRoles(parseRoles(p.maskRoles).length ? parseRoles(p.maskRoles) : ['BUSINESS_AGENT']);
        setMaskPhone(p.maskPhone ?? true);
        setMaskEmail(p.maskEmail ?? true);
        setMaskName(p.maskName ?? false);
        setRestrictToAssigned(p.restrictToAssigned ?? true);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const loadPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(await privacyService.preview({ maskPhone, maskEmail, maskName }));
    setPreviewing(false);
  }, [maskPhone, maskEmail, maskName]);

  // Re-read whenever a switch that changes the OUTPUT moves, so what is on
  // screen always describes the settings currently selected.
  useEffect(() => {
    if (!loading) loadPreview();
  }, [loading, loadPreview]);

  const toggleRole = (value: string) =>
    setRoles((r) => (r.includes(value) ? r.filter((x) => x !== value) : [...r, value]));

  const save = async () => {
    setSaving(true);
    setNote(null);
    const res = await privacyService.save({
      enabled,
      maskRoles: roles.join(','),
      maskPhone,
      maskEmail,
      maskName,
      restrictToAssigned,
    });
    setSaving(false);
    if (res.ok) {
      setPolicy(res.data);
      setNote({ kind: 'ok', text: enabled ? 'Saved. It applies from the next request.' : 'Saved. Nothing is masked.' });
    } else {
      setNote({ kind: 'err', text: res.error });
    }
  };

  // An enabled policy that names nobody masks nobody, while the switch reads
  // on. Caught here so the refusal is not a round trip.
  const blocked = enabled && roles.length === 0 ? 'Choose at least one role, or turn this off.' : null;

  if (loading) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</div>;
  }

  return (
    <div className="p-6 max-w-4xl space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Data privacy
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          Who in your organisation sees a customer&rsquo;s number, address and full name.
        </p>
      </div>

      {/* The honest caveat, stated where the decision is made rather than in a
          footnote nobody reaches. */}
      <div className="tl-card text-sm" style={{ color: 'var(--text-muted)' }}>
        Masking changes what the server sends, so a masked value is what reaches the
        browser &mdash; not something painted over it afterwards. It does not
        retrospectively hide anything already exported, and anyone who can sync leads to
        your CRM still has the originals there.
      </div>

      <div className="tl-card space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={enabled}
                 onChange={(e) => setEnabled(e.target.checked)} />
          <span>
            <span className="block font-medium text-gray-900 dark:text-white">
              Mask customer details for some roles
            </span>
            <span className="block text-sm" style={{ color: 'var(--text-muted)' }}>
              Off means everybody sees everything, which is how this organisation works today.
            </span>
          </span>
        </label>

        <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Applies to</p>
          <div className="space-y-2">
            {MASKABLE_ROLES.map((r) => (
              <label key={r.value} className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={roles.includes(r.value)}
                       onChange={() => toggleRole(r.value)} />
                <span>
                  <span className="block text-sm text-gray-900 dark:text-white">{r.label}</span>
                  <span className="block text-[12px]" style={{ color: 'var(--text-muted)' }}>{r.help}</span>
                </span>
              </label>
            ))}
          </div>

          <p className="text-sm font-medium text-gray-900 dark:text-white mt-4 mb-2">Hide</p>
          <div className="space-y-2">
            <Toggle checked={maskPhone} onChange={setMaskPhone}
                    label="Phone numbers"
                    help="Keeps the country code and last three digits, so a row can still be identified and discussed." />
            <Toggle checked={maskEmail} onChange={setMaskEmail}
                    label="Email addresses" help="Keeps the domain ending." />
            <Toggle checked={maskName} onChange={setMaskName}
                    label="Surnames"
                    help="First name is kept either way — somebody who cannot greet a caller cannot do the job." />
          </div>

          <p className="text-sm font-medium text-gray-900 dark:text-white mt-4 mb-2">And</p>
          <Toggle checked={restrictToAssigned} onChange={setRestrictToAssigned}
                  label="Show only leads assigned to them"
                  help="A separate question from masking: what they may READ about a person, versus WHICH people they see at all." />
        </div>
      </div>

      {/* What it actually does, on this organisation's own data. */}
      <div className="tl-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="font-medium text-gray-900 dark:text-white">On your most recent lead</p>
          <button type="button" onClick={loadPreview} disabled={previewing}
                  className="tl-btn tl-btn-neutral tl-btn-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${previewing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {!preview?.hasSample ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            No leads captured yet, so there is nothing to show masked.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Side title="Now" icon={<Eye className="w-3.5 h-3.5" />} sample={preview.before!} />
            <Side title="A masked role would see" icon={<EyeOff className="w-3.5 h-3.5" />}
                  sample={preview.after!} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving || !!blocked} className="tl-btn tl-btn-primary disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        {blocked && <span className="text-sm" style={{ color: 'var(--danger-text)' }}>{blocked}</span>}
        {!blocked && note && (
          <span className="text-sm"
                style={{ color: note.kind === 'err' ? 'var(--danger-text)' : 'var(--text-muted)' }}>
            {note.text}
          </span>
        )}
        {policy?.enabled && !enabled && (
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Unsaved: masking is currently on.
          </span>
        )}
      </div>
    </div>
  );
};

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  help: string;
}> = ({ checked, onChange, label, help }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>
      <span className="block text-sm text-gray-900 dark:text-white">{label}</span>
      <span className="block text-[12px]" style={{ color: 'var(--text-muted)' }}>{help}</span>
    </span>
  </label>
);

const Side: React.FC<{
  title: string;
  icon: React.ReactNode;
  sample: { phone: string | null; displayName: string | null; fields: Record<string, unknown> };
}> = ({ title, icon, sample }) => (
  <div className="rounded-lg border p-3 min-w-0" style={{ borderColor: 'var(--border)' }}>
    <p className="text-[11px] uppercase tracking-wide flex items-center gap-1.5 mb-2"
       style={{ color: 'var(--text-muted)' }}>
      {icon} {title}
    </p>
    <dl className="space-y-1.5 text-[12px]">
      <Row label="Name" value={sample.displayName} />
      <Row label="Phone" value={sample.phone} />
      {Object.entries(sample.fields || {}).slice(0, 6).map(([k, v]) => (
        <Row key={k} label={k} value={v == null ? null : String(v)} />
      ))}
    </dl>
  </div>
);

const Row: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="flex gap-2 min-w-0">
    <dt className="w-32 shrink-0 font-medium text-gray-700 dark:text-gray-200 truncate">{label}</dt>
    {/* Long answers wrap rather than stretch the column; a masked value is
        useless if it is cut off before the part that was kept. */}
    <dd className="min-w-0 break-words tabular-nums" style={{ color: 'var(--text-muted)' }}>
      {value && value !== 'null' ? value : '—'}
    </dd>
  </div>
);

export default DataPrivacy;
